/**
 * Import from spreadsheet modal — paste a TSV block, preview the parsed
 * rows, tweak anything wrong, then create the course + all trainees in
 * one click.
 *
 * Never sends welcome emails on import (attendees were emailed at booking).
 *
 * Guest rows are indented under their primary with a 🎟 chip so staff can
 * eyeball the register at a glance before hitting import.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  X, Upload, Loader2, CheckSquare, Square, AlertCircle, Ticket, ArrowRight,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { parseSpreadsheet, parseCourseTitle, buildImportPayload, computeRowPayments } from './spreadsheetImport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: 'probound', label: 'ProBound', color: '#2F5E96' },
  { key: 'proepoxy', label: 'ProEpoxy', color: '#7A0B7A' },
  { key: 'prodecor', label: 'ProDecor', color: '#D4A24B' },
];

function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

export default function SpreadsheetImportModal({ open, onClose, onImported, fixedCourse = null, initialRaw = '' }) {
  const [step, setStep] = useState(1); // 1 = paste, 2 = preview + course details
  const [raw, setRaw] = useState('');
  const [courseName, setCourseName] = useState('');
  const [courseDate, setCourseDate] = useState('');
  const [sections, setSections] = useState({ probound: false, proepoxy: false, prodecor: false });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState('new'); // 'new' | 'existing'
  const [existingId, setExistingId] = useState('');
  const [courses, setCourses] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (fixedCourse) { setTarget('existing'); setExistingId(fixedCourse.id); }
    if (initialRaw && !raw) { setRaw(initialRaw); parseAndContinue(initialRaw); }
  }, [open, fixedCourse, initialRaw]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || step !== 2 || courses || fixedCourse) return;
    fetch(`${API_URL}/api/staff/training-courses`, { credentials: 'include', headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setCourses((Array.isArray(list) ? list : []).sort((a, b) => (b.date || '').localeCompare(a.date || ''))))
      .catch(() => setCourses([]));
  }, [open, step, courses]);

  const reset = () => {
    setStep(1);
    setRaw('');
    setCourseName('');
    setCourseDate('');
    setSections({ probound: false, proepoxy: false, prodecor: false });
    setRows([]);
    setTarget('new');
    setExistingId('');
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const parseAndContinue = (text = raw) => {
    const parsed = parseSpreadsheet(text);
    if (parsed.rows.length === 0) {
      toast.error('No trainee rows detected', { description: 'Paste tab-separated data with names, phone, email, etc.' });
      return;
    }
    const meta = parseCourseTitle(parsed.courseTitle);
    // Only overwrite blank fields — staff might have typed something in
    // the header inputs before hitting parse.
    setCourseName((prev) => prev || meta.name);
    setCourseDate((prev) => prev || meta.date);
    setSections((prev) => {
      const already = Object.values(prev).some(Boolean);
      if (already) return prev;
      const next = { ...prev };
      meta.sections.forEach((s) => { next[s] = true; });
      return next;
    });
    setRows(parsed.rows);
    setStep(2);
  };

  const toggleRow = (idx, key) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: !r[key] } : r)));
  };
  const updateRow = (idx, key, value) => {
    // Recompute the derived payment fields (paymentHistory + totals) any
    // time a cell that feeds them changes so the preview always mirrors
    // what the payload builder will send. Cheap — one row at a time.
    const feedsPayments = ['deposit_paid', 'payment_method', 'booking_date',
      'balance_on_booking', 'notes', 'final_balance', 'payment_log'];
    setRows((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, [key]: value };
      if (feedsPayments.includes(key)) {
        Object.assign(next, computeRowPayments(next));
      }
      return next;
    }));
  };

  const includedCount = useMemo(() => rows.filter((r) => r.include).length, [rows]);
  const primaryCount = useMemo(() => rows.filter((r) => r.include && !r.is_guest).length, [rows]);
  const guestCount = useMemo(() => rows.filter((r) => r.include && r.is_guest).length, [rows]);
  const selectedSections = useMemo(
    () => Object.entries(sections).filter(([, v]) => v).map(([k]) => k),
    [sections],
  );

  const newCourseValid = courseName.trim().length >= 2 && /^\d{4}-\d{2}-\d{2}$/.test(courseDate) && selectedSections.length > 0;
  const canSubmit = !busy && includedCount > 0 && (target === 'existing' ? !!existingId : newCourseValid);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = buildImportPayload({
        course: { name: courseName, date: courseDate, sections: selectedSections },
        rows,
      });
      if (target === 'existing') { delete payload.course; payload.existing_course_id = existingId; }
      const r = await fetch(`${API_URL}/api/staff/training-courses/import`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      const parts = [];
      if (data.primaries_added) parts.push(`${data.primaries_added} bookers`);
      if (data.guests_added) parts.push(`${data.guests_added} plus-ones`);
      const skipped = (data.skipped || []).length;
      toast.success(`${data.course_created ? 'Course created' : `Added to ${data.course?.name || 'course'}`} · ${parts.join(' + ')}${skipped ? ` · ${skipped} already on register` : ''}`);
      if (onImported) onImported(data.course);
      handleClose();
    } catch (e) {
      toast.error('Import failed', { description: String(e.message || e).slice(0, 200) });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      data-testid="spreadsheet-import-modal"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
              <Upload className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-gray-900" data-testid="spreadsheet-import-title">
                {fixedCourse ? `Import spreadsheet into ${fixedCourse.name}` : 'Import course from spreadsheet'}
              </h2>
              <p className="text-xs text-gray-500">
                Step {step} of 2 · {step === 1 ? 'Paste from Google Sheets or Excel' : 'Review + import'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
            data-testid="spreadsheet-import-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {step === 1 ? (
            <PasteStep raw={raw} setRaw={setRaw} onContinue={() => parseAndContinue()} />
          ) : (
            <PreviewStep
              rows={rows}
              target={target}
              setTarget={setTarget}
              existingId={existingId}
              setExistingId={setExistingId}
              courses={courses}
              fixedCourse={fixedCourse}
              courseName={courseName}
              setCourseName={setCourseName}
              courseDate={courseDate}
              setCourseDate={setCourseDate}
              sections={sections}
              setSections={setSections}
              onToggleInclude={(i) => toggleRow(i, 'include')}
              onUpdateRow={updateRow}
              onBackToPaste={() => setStep(1)}
            />
          )}
        </div>

        {step === 2 && (
          <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-between bg-gray-50">
            <div className="text-xs text-gray-600" data-testid="import-summary">
              <span className="font-semibold text-gray-900">{primaryCount}</span> booker{primaryCount === 1 ? '' : 's'}
              {guestCount > 0 && (
                <>
                  {' · '}
                  <span className="font-semibold text-amber-800">{guestCount}</span> plus-one{guestCount === 1 ? '' : 's'}
                </>
              )}
              {' · '}welcome emails <strong>will NOT be sent</strong>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setStep(1)} disabled={busy} data-testid="import-back-btn">
                Back
              </Button>
              <Button
                onClick={submit}
                disabled={!canSubmit}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="import-submit"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Importing…</>
                ) : (
                  <>{target === 'existing' ? 'Add' : 'Create course & add'} {includedCount} trainee{includedCount === 1 ? '' : 's'}</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PasteStep({ raw, setRaw, onContinue }) {
  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-700">
        <p>Copy your course block from Google Sheets or Excel and paste it below. Include the course-name line above the columns.</p>
        <p className="text-xs text-gray-500 mt-1">
          Expected columns: <span className="font-mono">Name · Phone · Email · Deposit · Method · Booking Date · Balance · Notes · Final Balance · Payment log (optional)</span>
        </p>
        <p className="text-[11px] text-gray-500 mt-1">
          Payment log format (column J): <span className="font-mono">£120 Worldpay 21/04/2026; £354 BT 07/09/2026</span> — semicolon between entries. If you leave it blank, the parser will scan the Notes column for phrases like <em>"Paid £708 BT 20/08"</em>.
        </p>
      </div>
      <textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        rows={16}
        placeholder={"ProBound Mon 7th & Tue 8th September\n\nCustomer name\tNumber\tEmail\tDeposit Paid\tPayment Method\tBooking Date\tBalance on booking\tNotes\tFinal Balance\tPayment log\nPete Bell\t7780015262\tcmbah2@gmail.com\t£120.00\tXero/Invoice\t21/04/2026\t480\tPaid balance BT\t0\t£120 Xero 21/04/2026; £480 BT 20/08/2026"}
        className="w-full text-xs font-mono border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-500/60"
        data-testid="spreadsheet-import-textarea"
      />
      <div className="flex items-center justify-end">
        <Button
          onClick={onContinue}
          disabled={!raw.trim()}
          className="bg-emerald-600 hover:bg-emerald-500 text-white"
          data-testid="spreadsheet-import-parse-btn"
        >
          Parse
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

const fmtCourseDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'Date TBC');

function PreviewStep({
  rows, target, setTarget, existingId, setExistingId, courses, fixedCourse,
  courseName, setCourseName, courseDate, setCourseDate, sections, setSections,
  onToggleInclude, onUpdateRow, onBackToPaste,
}) {
  const pill = (on) => `text-xs font-semibold border rounded-full px-3 py-1.5 transition ${on ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 bg-white border-gray-300 hover:border-gray-400'}`;
  return (
    <div className="space-y-4">
      {fixedCourse ? (
        <p className="text-sm text-gray-700 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="import-fixed-course">
          Adding to <strong>{fixedCourse.name}</strong>. Anyone whose email is already on this register is skipped, and no welcome emails are sent.
        </p>
      ) : (
      <div className="flex flex-wrap items-center gap-2" data-testid="import-target">
        <span className="text-xs font-semibold text-gray-700">Add these trainees to:</span>
        <button type="button" onClick={() => setTarget('new')} className={pill(target === 'new')} aria-pressed={target === 'new'} data-testid="import-target-new">A new course</button>
        <button type="button" onClick={() => setTarget('existing')} className={pill(target === 'existing')} aria-pressed={target === 'existing'} data-testid="import-target-existing">An existing course</button>
      </div>
      )}

      {fixedCourse ? null : target === 'existing' ? (
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Course</label>
          <select
            value={existingId}
            onChange={(e) => setExistingId(e.target.value)}
            className="w-full md:w-2/3 text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            data-testid="import-existing-course"
          >
            <option value="">{courses === null ? 'Loading courses…' : courses.length ? 'Pick a course…' : 'No courses yet — create one instead'}</option>
            {(courses || []).map((c) => (
              <option key={c.id} value={c.id}>{c.name} — {fmtCourseDate(c.date)}{c.registrations_total != null ? ` · ${c.registrations_total} on register` : ''}</option>
            ))}
          </select>
          <p className="text-[11px] text-gray-500 mt-1">Anyone whose email is already on that register is skipped, so it's safe to re-run.</p>
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Course name</label>
          <input
            value={courseName}
            onChange={(e) => setCourseName(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            data-testid="import-course-name"
            placeholder="ProBound Mon 7th & Tue 8th September"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Course date</label>
          <input
            type="date"
            value={courseDate}
            onChange={(e) => setCourseDate(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            data-testid="import-course-date"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Sections (grants)</label>
          <div className="flex flex-wrap gap-1.5">
            {SECTIONS.map((s) => {
              const on = sections[s.key];
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSections((prev) => ({ ...prev, [s.key]: !prev[s.key] }))}
                  className={`text-xs font-semibold border rounded-full px-2.5 py-1.5 transition ${
                    on ? 'text-white border-transparent' : 'text-gray-700 bg-white border-gray-300 hover:border-gray-400'
                  }`}
                  style={on ? { backgroundColor: s.color } : {}}
                  data-testid={`import-section-${s.key}`}
                  aria-pressed={on}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* Row preview */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-semibold text-gray-800">
            {rows.length} row{rows.length === 1 ? '' : 's'} parsed
          </label>
          <button
            type="button"
            onClick={onBackToPaste}
            className="text-xs font-semibold text-gray-600 hover:text-gray-900"
            data-testid="import-repaste-btn"
          >
            Re-paste
          </button>
        </div>
        {/* Legend — explains the D/J/N badges next to each parsed payment
            so staff instantly know whether a row came from the Deposit
            column, the explicit Payment log, or was inferred from Notes. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-gray-500 mb-1.5">
          Payment source:
          <span className="inline-flex items-center gap-1"><span className="font-bold uppercase rounded px-1 bg-sky-100 text-sky-800">D</span>Deposit (col D)</span>
          <span className="inline-flex items-center gap-1"><span className="font-bold uppercase rounded px-1 bg-emerald-100 text-emerald-800">J</span>Payment log (col J)</span>
          <span className="inline-flex items-center gap-1"><span className="font-bold uppercase rounded px-1 bg-amber-100 text-amber-800">N</span>Inferred from Notes (col H)</span>
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="px-2 py-2 w-8"></th>
                  <th className="px-2 py-2">Name</th>
                  <th className="px-2 py-2">Phone</th>
                  <th className="px-2 py-2">Email</th>
                  <th className="px-2 py-2">Deposit</th>
                  <th className="px-2 py-2">Method</th>
                  <th className="px-2 py-2">Booked</th>
                  <th className="px-2 py-2">Balance now</th>
                  <th className="px-2 py-2">Payments parsed</th>
                  <th className="px-2 py-2">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r, i) => {
                  const primaryName = r.is_guest && r.guest_of_index !== null
                    ? rows[r.guest_of_index]?.name
                    : null;
                  return (
                    <tr
                      key={`${r.email || r.name || ""}-${i}`}
                      className={`${r.include ? '' : 'opacity-40'} ${r.is_guest ? 'bg-amber-50/40' : ''}`}
                      data-testid={`import-row-${i}`}
                    >
                      <td className="px-2 py-1.5">
                        <button
                          type="button"
                          onClick={() => onToggleInclude(i)}
                          data-testid={`import-row-toggle-${i}`}
                          aria-label={r.include ? 'Exclude this row' : 'Include this row'}
                        >
                          {r.include
                            ? <CheckSquare className="w-4 h-4 text-emerald-600" />
                            : <Square className="w-4 h-4 text-gray-400" />}
                        </button>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <div className={`flex items-center gap-1.5 ${r.is_guest ? 'pl-4' : ''}`}>
                          {r.is_guest && <Ticket className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                          <input
                            value={r.name}
                            onChange={(e) => onUpdateRow(i, 'name', e.target.value)}
                            className="w-40 border border-transparent hover:border-gray-300 focus:border-emerald-400 focus:outline-none rounded px-1 py-0.5 font-medium text-gray-900"
                          />
                        </div>
                        {r.guest_actual_name && (
                          <div className="text-[10px] text-amber-800 pl-6 mt-0.5">
                            attendee: <strong>{r.guest_actual_name}</strong>
                          </div>
                        )}
                        {r.is_guest && primaryName && (
                          <div className="text-[10px] text-gray-500 pl-6 mt-0.5">+1 with {primaryName}</div>
                        )}
                        {r.issues.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1 pl-6">
                            {r.issues.map((iss) => (
                              <span
                                key={iss}
                                className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 bg-amber-100 rounded-full px-1.5 py-0.5"
                              >
                                <AlertCircle className="w-2.5 h-2.5" /> {iss.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <input
                          value={r.phone}
                          onChange={(e) => onUpdateRow(i, 'phone', e.target.value)}
                          className="w-28 border border-transparent hover:border-gray-300 focus:border-emerald-400 focus:outline-none rounded px-1 py-0.5 text-gray-800 font-mono"
                        />
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <input
                          value={r.email}
                          onChange={(e) => onUpdateRow(i, 'email', e.target.value)}
                          className="w-52 border border-transparent hover:border-gray-300 focus:border-emerald-400 focus:outline-none rounded px-1 py-0.5 text-gray-800"
                        />
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-800">{r.deposit_paid || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">{r.payment_method || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">{r.booking_date || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-gray-800">
                        {r.balanceOwed === null || r.balanceOwed === undefined ? (
                          <span className="text-gray-400">—</span>
                        ) : r.balanceOwed <= 0 ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-800 bg-emerald-100 rounded px-1.5 py-0.5"
                            data-testid={`import-row-paid-${i}`}
                            title="Final Balance = £0 — trainee is fully paid"
                          >
                            Paid
                          </span>
                        ) : (
                          <span
                            className="font-semibold text-rose-700"
                            data-testid={`import-row-owed-${i}`}
                            title="Amount still owing today (col I Final Balance)"
                          >
                            £{Number(r.balanceOwed).toFixed(2)}
                          </span>
                        )}
                        {r.balance_on_booking && Number(r.balance_on_booking) !== Number(r.balanceOwed || 0) && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            was £{Number(r.balance_on_booking).toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 align-top">
                        {r.paymentHistory && r.paymentHistory.length > 0 ? (
                          <div className="space-y-0.5" data-testid={`import-row-payments-${i}`}>
                            {r.paymentHistory.map((h, hi) => {
                              const sourceTone = h.source === 'deposit' ? 'bg-sky-100 text-sky-800'
                                : h.source === 'log' ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-amber-100 text-amber-800';
                              const sourceLabel = h.source === 'deposit' ? 'D'
                                : h.source === 'log' ? 'J'
                                : 'N';
                              const sourceTitle = h.source === 'deposit' ? 'From Deposit column (D)'
                                : h.source === 'log' ? 'From Payment log column (J)'
                                : 'Parsed from Notes column (H)';
                              return (
                                <div key={hi} className="flex items-center gap-1 text-[10px] whitespace-nowrap" data-testid={`import-row-payments-${i}-${hi}`}>
                                  <span className={`font-bold uppercase rounded px-1 ${sourceTone}`} title={sourceTitle}>{sourceLabel}</span>
                                  <span className="font-semibold text-gray-900 tabular-nums">£{Number(h.amount).toFixed(2)}</span>
                                  {h.method && <span className="text-gray-500">· {h.method}</span>}
                                  {h.at && <span className="text-gray-400">· {h.at}</span>}
                                </div>
                              );
                            })}
                            {typeof r.paymentTotal === 'number' && (
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                Total fee: <strong className="text-gray-700">£{r.paymentTotal.toFixed(2)}</strong>
                                {typeof r.paymentReceived === 'number' && (
                                  <> · paid <strong className="text-gray-700">£{r.paymentReceived.toFixed(2)}</strong></>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-gray-500 max-w-xs truncate" title={r.notes}>{r.notes || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
